
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { getLicenseTextTemplate } from "./licenseTextTemplates";

interface LicenseData {
  clipName: string;
  downloadDate: string;
  exactDownloadTimestamp: string;
  licenseName: string;
  licenseVariables: {
    rightsholder: string;
    mentions: string[];
    hashtags: string[];
    // New configurable variables
    clip_language: string;
    license_validity_date?: string;
    geographic_zone: string;
    authorized_platforms: string[];
    max_extract_duration?: number;
    native_monetization_allowed: boolean;
    ai_editing_tools_allowed: boolean;
    ai_visual_transformation_allowed: boolean;
    ai_training_database_allowed: boolean;
    challenge_participation: boolean;
  };
  permissions?: string[];
  restrictions?: string[];
}

export const generateLicensePDF = async (
  licenseData: LicenseData,
  userName: string,
  language: 'en' | 'fr'
): Promise<void> => {
  const isEnglish = language === 'en';
  const currentDate = new Date().toLocaleString(isEnglish ? 'en-US' : 'fr-FR');
  const downloadDateTime = new Date(licenseData.exactDownloadTimestamp).toLocaleString(isEnglish ? 'en-US' : 'fr-FR');
  
  const translations = {
    title: isEnglish ? 'License Certificate' : 'Certificat de Licence',
    subtitle: isEnglish ? 'Official record of license terms agreed to at the time of download' : 'Enregistrement officiel des conditions de licence acceptées au moment du téléchargement',
    downloadInfo: isEnglish ? 'Download Information' : 'Informations du Téléchargement',
    clipName: isEnglish ? 'Clip Name:' : 'Nom du clip:',
    downloadDate: isEnglish ? 'Download Date:' : 'Date de téléchargement:',
    creator: isEnglish ? 'Creator:' : 'Créateur:',
    licenseType: isEnglish ? 'License Type:' : 'Type de licence:',
    rightsHolder: isEnglish ? 'Rights Holder:' : 'Ayant droit:',
    requiredAttribution: isEnglish ? 'Required Attribution' : 'Attribution Requise',
    requiredMentions: isEnglish ? 'Required Mentions:' : 'Mentions Requises:',
    requiredHashtags: isEnglish ? 'Required Hashtags:' : 'Hashtags Requis:',
    licenseConfiguration: isEnglish ? 'License Configuration' : 'Configuration de Licence',
    licenseTerms: isEnglish ? 'License Terms Summary' : 'Résumé des Conditions',
    permissions: isEnglish ? 'Permissions:' : 'Autorisations:',
    restrictions: isEnglish ? 'Restrictions:' : 'Restrictions:',
    completeAgreement: isEnglish ? 'COMPLETE LICENSE AGREEMENT' : 'ACCORD DE LICENCE COMPLET',
    generatedOn: isEnglish ? 'Generated on:' : 'Généré le:',
    footerText: isEnglish 
      ? 'This document was automatically generated and serves as an official record of the license terms agreed to at the time of download.'
      : 'Ce document a été généré automatiquement et sert de trace officielle des conditions de licence acceptées au moment du téléchargement.'
  };

  // Get the full license text using the static template with enhanced variables
  const fullLicenseHtml = getLicenseTextTemplate(
    licenseData.licenseVariables,
    licenseData.permissions || [],
    licenseData.restrictions || [],
    language
  );

  // Generate license configuration HTML for the summary
  const licenseConfigHtml = `
    <div style="margin-bottom: 30px; border: 1px solid #ddd; padding: 20px; background-color: #f8fafc;">
      <h2 style="font-size: 18px; margin-bottom: 15px; font-weight: bold; color: #000;">
        ${translations.licenseConfiguration}
      </h2>
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px; font-size: 12px;">
        <div><strong>${isEnglish ? 'Content Language:' : 'Langue du contenu:'}</strong><br/>${licenseData.licenseVariables.clip_language}</div>
        ${licenseData.licenseVariables.license_validity_date ? `<div><strong>${isEnglish ? 'Valid Until:' : 'Valide jusqu\'au:'}</strong><br/>${new Date(licenseData.licenseVariables.license_validity_date).toLocaleDateString(isEnglish ? 'en-US' : 'fr-FR')}</div>` : ''}
        <div><strong>${isEnglish ? 'Geographic Zone:' : 'Zone géographique:'}</strong><br/>${licenseData.licenseVariables.geographic_zone}</div>
        <div><strong>${isEnglish ? 'Authorized Platforms:' : 'Plateformes autorisées:'}</strong><br/>${licenseData.licenseVariables.authorized_platforms.join(', ')}</div>
        ${licenseData.licenseVariables.max_extract_duration ? `<div><strong>${isEnglish ? 'Max Extract Duration:' : 'Durée max d\'extrait:'}</strong><br/>${licenseData.licenseVariables.max_extract_duration}s</div>` : ''}
        <div><strong>${isEnglish ? 'Native Monetization:' : 'Monétisation native:'}</strong><br/><span style="color: ${licenseData.licenseVariables.native_monetization_allowed ? '#059669' : '#dc2626'};">${licenseData.licenseVariables.native_monetization_allowed ? (isEnglish ? 'Allowed' : 'Autorisée') : (isEnglish ? 'Not allowed' : 'Non autorisée')}</span></div>
        <div><strong>${isEnglish ? 'AI Editing Tools:' : 'Outils d\'édition IA:'}</strong><br/><span style="color: ${licenseData.licenseVariables.ai_editing_tools_allowed ? '#059669' : '#dc2626'};">${licenseData.licenseVariables.ai_editing_tools_allowed ? (isEnglish ? 'Allowed' : 'Autorisés') : (isEnglish ? 'Not allowed' : 'Non autorisés')}</span></div>
        <div><strong>${isEnglish ? 'AI Visual Transform:' : 'Transformation visuelle IA:'}</strong><br/><span style="color: ${licenseData.licenseVariables.ai_visual_transformation_allowed ? '#059669' : '#dc2626'};">${licenseData.licenseVariables.ai_visual_transformation_allowed ? (isEnglish ? 'Allowed' : 'Autorisée') : (isEnglish ? 'Not allowed' : 'Non autorisée')}</span></div>
        <div><strong>${isEnglish ? 'AI Training Database:' : 'Base d\'entraînement IA:'}</strong><br/><span style="color: ${licenseData.licenseVariables.ai_training_database_allowed ? '#059669' : '#dc2626'};">${licenseData.licenseVariables.ai_training_database_allowed ? (isEnglish ? 'Allowed' : 'Autorisée') : (isEnglish ? 'Not allowed' : 'Non autorisée')}</span></div>
        <div><strong>${isEnglish ? 'Challenge Participation:' : 'Participation aux défis:'}</strong><br/><span style="color: ${licenseData.licenseVariables.challenge_participation ? '#059669' : '#dc2626'};">${licenseData.licenseVariables.challenge_participation ? (isEnglish ? 'Allowed' : 'Autorisée') : (isEnglish ? 'Not allowed' : 'Non autorisée')}</span></div>
      </div>
    </div>
  `;

  const htmlContent = `
    <div style="font-family: Arial, sans-serif; font-size: 12px; line-height: 1.4; color: #000; max-width: 210mm; margin: 0 auto; padding: 20mm;">
      <!-- Header -->
      <div style="text-align: center; margin-bottom: 30px; border-bottom: 2px solid #000; padding-bottom: 20px;">
        <h1 style="font-size: 24px; margin-bottom: 10px; font-weight: bold; color: #000;">
          ${translations.title}
        </h1>
        <p style="font-size: 14px; color: #333; margin: 0;">
          ${translations.subtitle}
        </p>
      </div>
      
      <!-- Download Information -->
      <div style="margin-bottom: 30px; border: 1px solid #ddd; padding: 20px; background-color: #f9f9f9;">
        <h2 style="font-size: 18px; margin-bottom: 15px; font-weight: bold; color: #000;">
          ${translations.downloadInfo}
        </h2>
        <table style="width: 100%; border-collapse: collapse;">
          <tr><td style="padding: 8px 0; font-weight: bold; width: 40%;">${translations.clipName}</td><td style="padding: 8px 0;">${licenseData.clipName}</td></tr>
          <tr><td style="padding: 8px 0; font-weight: bold;">${translations.downloadDate}</td><td style="padding: 8px 0;">${downloadDateTime}</td></tr>
          <tr><td style="padding: 8px 0; font-weight: bold;">${translations.creator}</td><td style="padding: 8px 0;">${userName}</td></tr>
          <tr><td style="padding: 8px 0; font-weight: bold;">${translations.licenseType}</td><td style="padding: 8px 0;">${licenseData.licenseName}</td></tr>
          <tr><td style="padding: 8px 0; font-weight: bold;">${translations.rightsHolder}</td><td style="padding: 8px 0;">${licenseData.licenseVariables.rightsholder}</td></tr>
        </table>
      </div>

      ${licenseConfigHtml}
      
      ${(licenseData.licenseVariables.mentions?.length > 0 || licenseData.licenseVariables.hashtags?.length > 0) ? `
      <!-- Required Attribution -->
      <div style="margin-bottom: 30px; border: 1px solid #ddd; padding: 20px;">
        <h2 style="font-size: 18px; margin-bottom: 15px; font-weight: bold; color: #000;">
          ${translations.requiredAttribution}
        </h2>
        ${licenseData.licenseVariables.mentions?.length > 0 ? `
          <div style="margin-bottom: 15px;">
            <h3 style="font-size: 14px; font-weight: bold; margin-bottom: 8px; color: #333;">
              ${translations.requiredMentions}
            </h3>
            <div style="background-color: #f0f0f0; padding: 10px; border-radius: 4px;">
              ${licenseData.licenseVariables.mentions.map(mention => `<span style="display: inline-block; background: #e0e0e0; padding: 4px 8px; margin: 2px; border-radius: 3px; font-family: monospace;">@${mention}</span>`).join('')}
            </div>
          </div>
        ` : ''}
        ${licenseData.licenseVariables.hashtags?.length > 0 ? `
          <div>
            <h3 style="font-size: 14px; font-weight: bold; margin-bottom: 8px; color: #333;">
              ${translations.requiredHashtags}
            </h3>
            <div style="background-color: #f0f0f0; padding: 10px; border-radius: 4px;">
              ${licenseData.licenseVariables.hashtags.map(hashtag => `<span style="display: inline-block; background: #e0e0e0; padding: 4px 8px; margin: 2px; border-radius: 3px; font-family: monospace;">#${hashtag}</span>`).join('')}
            </div>
          </div>
        ` : ''}
      </div>
      ` : ''}
      
      ${licenseData.permissions?.length > 0 || licenseData.restrictions?.length > 0 ? `
      <!-- License Terms Summary -->
      <div style="margin-bottom: 30px; border: 1px solid #ddd; padding: 20px;">
        <h2 style="font-size: 18px; margin-bottom: 15px; font-weight: bold; color: #000;">
          ${translations.licenseTerms}
        </h2>
        ${licenseData.permissions?.length > 0 ? `
          <div style="margin-bottom: 15px;">
            <h3 style="font-size: 14px; font-weight: bold; margin-bottom: 8px; color: #009900;">
              ${translations.permissions}
            </h3>
            <ul style="margin: 0; padding-left: 20px;">
              ${licenseData.permissions.map(permission => `<li style="margin-bottom: 4px;">${permission}</li>`).join('')}
            </ul>
          </div>
        ` : ''}
        ${licenseData.restrictions?.length > 0 ? `
          <div>
            <h3 style="font-size: 14px; font-weight: bold; margin-bottom: 8px; color: #cc0000;">
              ${translations.restrictions}
            </h3>
            <ul style="margin: 0; padding-left: 20px;">
              ${licenseData.restrictions.map(restriction => `<li style="margin-bottom: 4px;">${restriction}</li>`).join('')}
            </ul>
          </div>
        ` : ''}
      </div>
      ` : ''}
      
      <div style="margin-bottom: 30px; border: 2px solid #333; padding: 20px;">
        <h2 style="font-size: 18px; margin-bottom: 20px; font-weight: bold; color: #000; text-align: center; border-bottom: 1px solid #333; padding-bottom: 10px;">
          ${translations.completeAgreement}
        </h2>
        
        <div style="font-size: 11px; line-height: 1.5;">
          ${fullLicenseHtml}
        </div>
      </div>
      
      <!-- Certificate Footer -->
      <div style="margin-top: 40px; padding-top: 20px; border-top: 2px solid #000; text-align: center;">
        <p style="font-size: 10px; font-style: italic; color: #666; margin: 0;">
          ${translations.footerText}
        </p>
        <p style="font-size: 10px; color: #666; margin: 5px 0 0 0;">
          ${translations.generatedOn} ${currentDate}
        </p>
      </div>
    </div>
  `;

  // Create temporary container for PDF generation
  const tempContainer = document.createElement('div');
  tempContainer.style.position = 'absolute';
  tempContainer.style.left = '-9999px';
  tempContainer.style.top = '0';
  tempContainer.style.width = '210mm';
  tempContainer.style.backgroundColor = 'white';
  tempContainer.innerHTML = htmlContent;
  document.body.appendChild(tempContainer);

  try {
    // Generate PDF with optimized settings
    const canvas = await html2canvas(tempContainer, {
      scale: 1.5,
      useCORS: true,
      allowTaint: true,
      backgroundColor: "#FFFFFF",
      width: 794, // A4 width at 96 DPI
      windowWidth: 794,
      logging: false
    });

    // Create PDF
    const pdf = new jsPDF({
      format: 'a4',
      unit: 'mm',
      orientation: 'portrait'
    });

    // Convert canvas to image and add to PDF
    const imgData = canvas.toDataURL('image/png', 0.95);
    const imgWidth = 210; // A4 width in mm
    const pageHeight = 295; // A4 height in mm minus margins
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;

    // Add first page
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight, undefined, 'FAST');
    heightLeft -= pageHeight;

    // Add additional pages if needed
    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight, undefined, 'FAST');
      heightLeft -= pageHeight;
    }

    // Generate filename
    const cleanClipName = licenseData.clipName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
    const dateStr = new Date().toISOString().split('T')[0];
    const filename = `historical_license_${cleanClipName}_${dateStr}.pdf`;

    // Save PDF
    pdf.save(filename);

    console.log("PDF generated successfully with enhanced license configuration");
  } catch (error) {
    console.error("Error generating PDF:", error);
    throw error;
  } finally {
    // Clean up
    document.body.removeChild(tempContainer);
  }
};
